import _ from 'lodash';
import axios from 'axios';
import config from 'client/shared/config';

import {
	observable,
	computed,
	action,
	runInAction
} from 'mobx';

import { getAx } from 'shared/util';

const STATE_IDLE    = 0;
const STATE_LOADING = 1;
const STATE_SUCCESS = 2;
const STATE_ERROR   = 3;

class APIStore {

	/**
	 * Private Members
	 */

	_userID = '';

	get userID () {
		 return this._userID;
	}

	set userID (userID) {
		this._userID = userID;
	}

	/**
	 * Observables & Computeds
	 */

	@observable
	data = {
		stats: {},
		user: {},
		artist: {},
		songs: [],
		albums: []
	};

	@observable
	_dataState = STATE_IDLE;

	@observable
	_progressLoaded = 0

	@observable
	_progressTotal = 1;

	@computed
	get isIdle () {
		return this._dataState === STATE_IDLE;
	}

	@computed
	get isLoading () {
		return this._dataState === STATE_LOADING;
	}

	@computed
	get isSuccess () {
		return this._dataState === STATE_SUCCESS;
	}

	@computed
	get isError () {
		return this._dataState === STATE_ERROR;
	}

	get ax () {
		return getAx();
	}

	/**
	 * Actions
	 */

	@action
	async getInitialPayload () {
		if (this.isLoading) {

			return;
		}

		try {

			this._dataState = STATE_LOADING;

			const route = config.getRoute('profile');
			const ax = getAx();
			const { data } = await ax.get(route);

			apiStore.userID = data.user.id;

			runInAction('Update with initial payload', () => {
				this.data = data;
				this._dataState = STATE_SUCCESS;
			});
		} catch (error) {

			const { response } = error;

			if (_.isEmpty(response)) {

				throw error;
			}

			console.log(error);
			this._dataState = STATE_ERROR;

			if (response.status === 401) {

				// 401 handling is centralized in the axios interceptor
				// ([util/get-ax.js]). Don't double-fire navigation here —
				// two redirects racing each other contributed to an
				// ERR_CONNECTION_CLOSED on the post-logout page load.
				return;
			}
		}
	}

	@action
	async fetch ({ route }) {
		this._dataState = STATE_LOADING;

		try {

			const ax = getAx();
			const { data } = await ax.get(route);
			this._dataState = STATE_SUCCESS;
			return data;
		} catch (err) {

			console.error('[ERROR: apiStore.fetch]', err);
			this._dataState = STATE_ERROR;
		}
	}

	@action
	async save ({ route, payload, method = 'patch', silent = false }) {
		const ax = getAx();

		let id = payload.id || payload._id;

		if (_.isEmpty(id) && _.isFunction(payload.get)) {

			id = payload.get('_id');
		}

		const methodName = _.isEmpty(id) ? 'post' : method;
		const shouldAppendID = _.isEmpty(id) !== true && route.indexOf(id) < 0;
		const url = shouldAppendID ? `${route}/${id}` : route;

		if (silent !== true) {

			console.log(`[API Store save()] this._dataState = ${STATE_LOADING}`);
			this._dataState = STATE_LOADING;
		}

		if (!ax[methodName]) {

			throw new Error(`[API Store.save] Method ${methodName} not found in axios.`);
		}

		const { data } = await ax[methodName](url, payload);

		console.log('[API Store.save DATA]', data);

		if (silent !== true) {

			console.log(`[API Store save()] this._dataState = ${STATE_SUCCESS}`);
			this._dataState = STATE_SUCCESS;
		}

		return data;
	}

	@action
	async patch ({ route, payload, silent = false }) {
		const ax = getAx();

		let id = payload.id || payload._id;

		if (_.isEmpty(id) && _.isFunction(payload.get)) {

			id = payload.get('_id');
		}

		const shouldAppendID = _.isEmpty(id) !== true && route.indexOf(id) < 0;
		const url = shouldAppendID ? `${route}/${id}` : route;

		if (silent !== true) {

			console.log(`[API Store save()] this._dataState = ${STATE_LOADING}`);
			this._dataState = STATE_LOADING;
		}

		const { data } = await ax.patch(url, payload);
		console.log('[API Store.save DATA]', data);

		if (silent !== true) {

			console.log(`[API Store save()] this._dataState = ${STATE_SUCCESS}`);
			this._dataState = STATE_SUCCESS;
		}

		return data;
	}

	@action
	async delete ({ route }) {
		this._dataState = STATE_LOADING;

		try {

			const ax = getAx();
			const { data } = await ax.delete(route);

			this._dataState = STATE_SUCCESS;
			return data;
		} catch (err) {

			console.error('[ERROR: apiStore.delete]', err);
			this._dataState = STATE_ERROR;
		}
	}
};

const apiStore = new APIStore();
export default apiStore;
